import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import publicApi from "@/lib/publicApi";
import { readError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import HCaptchaWidget from "@/components/HCaptchaWidget";
import { Loader2, Send, CheckCircle2, AlertTriangle, MessageSquare } from "lucide-react";

/**
 * Inquiry-first CTA form used on every instrument sub-page when commerce is OFF.
 * Reuses the existing /api/inquiries backend endpoint. Subject is auto-generated
 * from the product name so Mario immediately knows which instrument was asked about.
 *
 * Props:
 *   product        — { id, name, base_price_eur }
 *   locale         — "hr" | "en" | "de" | "fr"
 */
export default function ProductInquiryForm({ product, locale = "hr" }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const captchaRef = useRef(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!product) return;
    setError("");
    setBusy(true);
    try {
      const hcaptcha_token = captchaRef.current?.getToken() || "";
      if (captchaRef.current?.isConfigured() && !hcaptcha_token) {
        setError(t("inquiry_form.captcha_required"));
        setBusy(false);
        return;
      }
      await publicApi.post("/inquiries", {
        product_slug: product.id,
        configuration: [],
        base_price_eur: product.base_price_eur || 0,
        total_price_eur: product.base_price_eur || 0,
        customer: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          preferred_contact: "email",
        },
        message: form.message,
        locale,
        hcaptcha_token,
        website: form.website,
        inquiry_source: "contact_form",
        subject: `Upit: ${product.name}`,
      });
      setDone(true);
    } catch (err) {
      setError(readError(err, t("inquiry_form.error")));
      captchaRef.current?.reset?.();
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="inquiry-form-success"
        className="rounded-2xl border border-primary/25 bg-primary/5 p-8 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" strokeWidth={1.2} />
        <h3 className="font-serif text-2xl text-foreground">{t("inquiry_form.success_title")}</h3>
        <p className="mt-3 text-sm text-foreground/70 max-w-md mx-auto leading-relaxed">
          {t("inquiry_form.success_body")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-border/60 bg-card p-6 lg:p-8 shadow-ambient"
      data-testid="product-inquiry-form"
    >
      <div className="flex items-baseline gap-2 mb-1">
        <MessageSquare className="h-4 w-4 text-primary" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl text-foreground">{t("inquiry_form.title")}</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
        {t("inquiry_form.subtitle_for", { product: product?.name || "" })}
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("inquiry_form.name")}</Label>
          <Input required value={form.name} disabled={busy} onChange={onChange("name")} className="h-10 bg-background" data-testid="inquiry-form-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("inquiry_form.email")}</Label>
          <Input required type="email" value={form.email} disabled={busy} onChange={onChange("email")} className="h-10 bg-background" data-testid="inquiry-form-email" />
        </div>
      </div>
      <div className="space-y-1.5 mt-3">
        <Label className="text-xs">{t("inquiry_form.phone")} <span className="text-muted-foreground">({t("inquiry_form.optional")})</span></Label>
        <Input value={form.phone} disabled={busy} onChange={onChange("phone")} className="h-10 bg-background" data-testid="inquiry-form-phone" />
      </div>
      <div className="space-y-1.5 mt-3">
        <Label className="text-xs">{t("inquiry_form.message")}</Label>
        <Textarea
          required
          minLength={10}
          value={form.message}
          disabled={busy}
          onChange={onChange("message")}
          rows={4}
          className="bg-background resize-none"
          placeholder={t("inquiry_form.message_placeholder")}
          data-testid="inquiry-form-message"
        />
      </div>
      <div className="mt-4">
        <HCaptchaWidget ref={captchaRef} />
      </div>
      {/* Honeypot */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={form.website}
        onChange={onChange("website")}
        className="absolute opacity-0 -left-[9999px] h-0 w-0 pointer-events-none"
      />
      {error && (
        <div className="mt-4 text-sm rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive flex items-start gap-2" data-testid="inquiry-form-error">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}
      <div className="mt-5 flex items-center justify-end">
        <Button type="submit" disabled={busy} className="shadow-interactive" data-testid="inquiry-form-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" strokeWidth={1.5} />}
          {busy ? t("inquiry_form.sending") : t("inquiry_form.submit")}
        </Button>
      </div>
    </form>
  );
}
